/**
 * Calendar view for group events
 * Shows an interactive calendar with event indicators and a list of events for the selected day
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
} from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Event as EventIcon, LocationOn as LocationIcon } from '@mui/icons-material'
import { CalendarEventDto, getEventStatusLabel, getEventStatusColor } from '@/api/types/calendarEvent'
import { EventDayCell } from './EventDayCell'
import { isSameDay, parseISO, format } from 'date-fns'

interface EventsCalendarViewProps {
  events: CalendarEventDto[]
  selectedDate: Date | null
  onDateChange: (date: Date | null) => void
  /** Group ID for return navigation */
  groupId?: string
}

export function EventsCalendarView({
  events,
  selectedDate,
  onDateChange,
  groupId,
}: EventsCalendarViewProps) {
  const navigate = useNavigate()

  // Get events for the selected date
  const eventsOnSelectedDay = selectedDate
    ? events.filter((event) => {
        const eventDate = parseISO(event.startDateTime)
        return isSameDay(eventDate, selectedDate)
      })
    : []

  const handleEventClick = (eventId: string) => {
    // Include return URL to navigate back to group page
    const returnUrl = groupId ? `/groups/${groupId}?tab=events` : undefined
    const url = returnUrl
      ? `/events/${eventId}?returnTo=${encodeURIComponent(returnUrl)}`
      : `/events/${eventId}`
    navigate(url)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
      {/* Calendar */}
      <Paper sx={{ p: 2, flex: '0 0 auto' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={selectedDate}
            onChange={onDateChange}
            slots={{
              day: (dayProps) => <EventDayCell {...dayProps} events={events} />,
            }}
          />
        </LocalizationProvider>
      </Paper>

      {/* Events for selected day */}
      <Paper sx={{ p: 2, flex: 1, minHeight: 300 }}>
        <Typography variant="h6" gutterBottom>
          {selectedDate
            ? `Events on ${format(selectedDate, 'MMMM d, yyyy')}`
            : 'Select a date to view events'}
        </Typography>

        {selectedDate && eventsOnSelectedDay.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No events scheduled for this day
          </Typography>
        )}

        {eventsOnSelectedDay.length > 0 && (
          <List>
            {eventsOnSelectedDay.map((event, index) => (
              <Box key={event.id}>
                {index > 0 && <Divider variant="inset" component="li" />}
                <ListItem
                  component="div"
                  onClick={() => handleEventClick(event.id)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={event.coverImageUrl}
                      variant="rounded"
                      sx={{ bgcolor: 'primary.main' }}
                    >
                      <EventIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" component="span">
                          {event.title}
                        </Typography>
                        <Chip
                          label={getEventStatusLabel(event.status)}
                          color={getEventStatusColor(event.status)}
                          size="small"
                        />
                      </Box>
                    }
                    secondary={
                      <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" component="span">
                          {event.isAllDay
                            ? 'All day'
                            : `${format(parseISO(event.startDateTime), 'h:mm a')} - ${format(parseISO(event.endDateTime), 'h:mm a')}`}
                        </Typography>
                        {event.location?.name && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <LocationIcon sx={{ fontSize: 14 }} />
                            {event.location.name}
                          </Typography>
                        )}
                        {event.quickDescription && (
                          <Typography variant="body2" color="text.secondary" component="span">
                            {event.quickDescription}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  )
}
